import { useEffect, useRef, useState } from "react";
import { Volume2, Pause, Play, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const supported = typeof window !== "undefined" && "speechSynthesis" in window;

function clean(text = "") {
  return String(text)
    .replace(/\*\*/g, "")
    .replace(/^#{1,4}\s+/gm, "")
    .replace(/^\s*[-*•]\s+/gm, "")
    .trim();
}

export default function AudioSummary({ text }) {
  const [playing, setPlaying] = useState(false);
  const [paused, setPaused] = useState(false);
  const uttRef = useRef(null);

  useEffect(() => () => { if (supported) window.speechSynthesis.cancel(); }, []);

  const pickVoice = () => {
    const voices = window.speechSynthesis.getVoices();
    return voices.find((v) => v.lang && v.lang.toLowerCase().startsWith("it")) || null;
  };

  const play = () => {
    if (!supported) { toast.error("Il tuo browser non supporta la sintesi vocale."); return; }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(clean(text));
    u.lang = "it-IT";
    const v = pickVoice();
    if (v) u.voice = v;
    u.rate = 1;
    u.onend = () => { setPlaying(false); setPaused(false); };
    u.onerror = () => { setPlaying(false); setPaused(false); };
    uttRef.current = u;
    window.speechSynthesis.speak(u);
    setPlaying(true);
    setPaused(false);
  };

  const togglePause = () => {
    if (paused) { window.speechSynthesis.resume(); setPaused(false); }
    else { window.speechSynthesis.pause(); setPaused(true); }
  };

  const stop = () => { window.speechSynthesis.cancel(); setPlaying(false); setPaused(false); };

  if (!supported) return null;

  return !playing ? (
    <Button data-testid="audio-play-button" variant="ghost" size="sm" className="gap-1.5 text-xs" onClick={play}>
      <Volume2 className="h-3.5 w-3.5" /> Ascolta
    </Button>
  ) : (
    <div className="flex items-center gap-1">
      <Button data-testid="audio-pause-button" variant="ghost" size="sm" className="gap-1.5 text-xs" onClick={togglePause}>
        {paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />} {paused ? "Riprendi" : "Pausa"}
      </Button>
      <Button data-testid="audio-stop-button" variant="ghost" size="icon" className="h-8 w-8" onClick={stop}>
        <Square className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
